import React, { useState, useEffect } from 'react';
import StandardPremiumModal from './StandardPremiumModal';
import { useBusiness } from '../../contexts/BusinessContext';
import ProfileTab from './FieldStaffTabs/ProfileTab';
import { toast } from 'react-hot-toast';
import AccessControlsTab from './FieldStaffTabs/AccessControlsTab';
import AssignmentsTab from './FieldStaffTabs/AssignmentsTab';
import ActivityLogTab from './FieldStaffTabs/ActivityLogTab';
import { fieldStaffAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const AddEditFieldStaffModal = ({ isOpen, onClose, staff, onSuccess }) => {
  const { activeBusiness } = useBusiness();
  const [activeTab, setActiveTab] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [roleTemplates, setRoleTemplates] = useState([]);
  const [formData, setFormData] = useState({
    // Profile
    full_name: '',
    mobile: '',
    email: '',
    employee_code: '',
    date_of_joining: '',
    notes: '',
    status: 'active',
    password: '',
    role_template: 'sales_rep', // Default to Sales Rep
    // Permissions
    permissions: {},
    // Assignments
    assignments: {
      stores: [],
      customers: [],
      areas: []
    }
  });

  // Fetch role templates on mount
  useEffect(() => {
    const fetchRoleTemplates = async () => {
      try {
        const { data } = await fieldStaffAPI.getRoleTemplates();
        setRoleTemplates(data.templates || []);

        // If creating new staff, set default permissions from default template
        if (!staff && data.templates?.length > 0) {
          const defaultTemplate = data.templates.find(t => t.id === 'sales_rep') || data.templates[0];
          setFormData(prev => ({
            ...prev,
            role_template: defaultTemplate.id,
            permissions: defaultTemplate.permissions
          }));
        }
      } catch (error) {
        toast.error(getApiErrorMessage(error, 'Failed to load role templates'));
      }
    };

    if (isOpen) {
      fetchRoleTemplates();
    }
  }, [isOpen, staff]);

  useEffect(() => {
    if (staff) {
      // Edit mode - populate existing data
      setFormData({
        full_name: staff.full_name || '',
        mobile: staff.mobile || '',
        email: staff.email || '',
        employee_code: staff.employee_code || '',
        date_of_joining: staff.date_of_joining || '',
        notes: staff.notes || '',
        status: staff.status || 'active',
        password: '', // Don't populate password
        role_template: staff.role_template || 'custom',
        permissions: staff.permissions || {},
        assignments: staff.assignments || { stores: [], customers: [], areas: [] }
      });
    } else {
      // Add mode - reset form with default template
      const defaultTemplate = roleTemplates.find(t => t.id === 'sales_rep');
      setFormData({
        full_name: '',
        mobile: '',
        email: '',
        employee_code: '',
        date_of_joining: '',
        notes: '',
        status: 'active',
        password: '',
        role_template: 'sales_rep',
        permissions: defaultTemplate?.permissions || {},
        assignments: { stores: [], customers: [], areas: [] }
      });
    }
    setActiveTab('profile');
  }, [staff, isOpen, roleTemplates]);

  const handleSave = async () => {
    // Validate required fields
    if (!formData.full_name.trim()) {
      toast.error('Full name is required');
      setActiveTab('profile');
      return;
    }
    if (!formData.mobile.trim()) {
      toast.error('Mobile number is required');
      setActiveTab('profile');
      return;
    }
    if (!staff && !formData.password.trim()) {
      toast.error('Password is required for new staff');
      setActiveTab('profile');
      return;
    }
    if (!staff && !formData.role_template) {
      toast.error('Please select a role template');
      setActiveTab('profile');
      return;
    }

    setSaving(true);
    try {
      // Prepare payload
      const payload = {
        full_name: formData.full_name,
        mobile: formData.mobile,
        email: formData.email || null,
        employee_code: formData.employee_code || null,
        date_of_joining: formData.date_of_joining || null,
        notes: formData.notes || null,
        status: formData.status,
        permissions: formData.permissions,
        assignments: formData.assignments,
        role_template: formData.role_template || 'custom'
      };

      if (!staff) {
        payload.password = formData.password;
      }

      if (staff) {
        await fieldStaffAPI.update(staff.id, payload);
      } else {
        await fieldStaffAPI.create(payload);
      }
      onSuccess();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error saving field staff'));
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile' },
    { id: 'access', label: 'Access Controls' },
    { id: 'assignments', label: 'Assignments' },
    ...(staff ? [{ id: 'activity', label: 'Activity Log' }] : [])
  ];

  const footer = (
    <>
      <button
        onClick={onClose}
        className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        disabled={saving}
        data-testid="staff-modal-cancel"
      >
        Cancel
      </button>
      <button
        onClick={handleSave}
        disabled={saving}
        className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium disabled:opacity-50"
        data-testid="staff-modal-save"
      >
        {saving ? 'Saving...' : staff ? 'Update' : 'Create Staff'}
      </button>
    </>
  );

  return (
    <StandardPremiumModal
      isOpen={isOpen}
      onClose={onClose}
      title={staff ? 'Edit Field Staff' : 'Add New Staff Member'}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      footer={footer}
      size="large"
    >
      {activeTab === 'profile' && (
        <ProfileTab
          formData={formData}
          setFormData={setFormData}
          isEditMode={!!staff}
          roleTemplates={roleTemplates}
        />
      )}
      {activeTab === 'access' && (
        <AccessControlsTab
          formData={formData}
          setFormData={setFormData}
          roleTemplates={roleTemplates}
        />
      )}
      {activeTab === 'assignments' && (
        <AssignmentsTab
          formData={formData}
          setFormData={setFormData}
        />
      )}
      {activeTab === 'activity' && staff && (
        <ActivityLogTab staffId={staff?.id} />
      )}
    </StandardPremiumModal>
  );
};

export default AddEditFieldStaffModal;
